import styles from "./styles.module.scss"
import cn from "clsx"
import dayjs from "dayjs"
import SbEditable from "storyblok-react"
import { Hyper, List, Media, Fade } from "components"
import { render } from "storyblok-rich-text-react-renderer"
import type { FC, HTMLAttributes } from "react"
import type { SbEditableContent } from "storyblok-react"
import type { RenderOptions } from "storyblok-rich-text-react-renderer"
import type { Position } from "storyblok/schema"


interface ItemProps
extends HTMLAttributes<HTMLLIElement>, SbEditableContent, Position {}

const formatDate = (date: string) => dayjs(date).format('MMM YYYY')
const COMPONENTS_MAP: RenderOptions = { nodeResolvers: {
  list_item: (children) => (
    <List.Item className={styles.listItem}>{children}</List.Item>
  ),
  bullet_list: (children) => (
    <List.Container className={styles.listContainer}>{children}</List.Container>
  ),
}}

export const Item: FC<ItemProps> = ({
  _uid, _editable, component,
  startDate, endDate, type, jobTitle, organization, description, isEducation,
  className,
  ...rest
}) => {
  const diffMonths = dayjs(endDate || Date.now()).diff(startDate || Date.now(), 'months')
  const startFormatDate = formatDate(startDate)
  const endFormatDate = endDate ? formatDate(endDate) : 'Present'
  const durationYears = Math.floor(diffMonths / 12)
  const durationMonths = diffMonths % 12
  const duration = [
    durationYears ? durationYears + 'y' : undefined,
    durationMonths ? durationMonths + 'm' : undefined,
  ].filter(t => t).join(' ')
  const propType = isEducation 
    ? 'EducationalOrganization' 
    : endDate 
      ? 'Event/Job' 
      : 'Organization'

  return (
    <SbEditable content={{ _uid, _editable, component }}>
      <li className={cn(styles.item, className, {[styles.education]: isEducation})} {...rest}
        itemScope itemProp={isEducation ? 'alumniOf' : 'worksFor'}
        itemType={`http://schema.org/${propType}`}>
        <Media from="desktopS">
          <Fade>
            <div className={styles.time}>
              <time dateTime={startDate} itemProp="startDate">
                {startFormatDate}
              </time><br />
              - <time dateTime={endDate} itemProp={endDate && 'endDate'}>
                {endFormatDate}
              </time><br />
              <span className={styles.type}>{type}</span>
              <span className={styles.duration}>({duration}{!endDate && '+'})</span>
            </div>
          </Fade>
        </Media>
        <div className={styles.line}>
          <div className={styles.circle} />
        </div>
        <Fade>
          <div className={styles.details}>
            <h3>
              <span itemProp={isEducation ? 'description' : 'jobTitle'}>{jobTitle} </span>
              <Hyper.Link href="#">@ 
                <span itemProp="name"> {organization}</span>
              </Hyper.Link>
            </h3>
            <Media till="desktopS">
              <p className={styles.timeCompact}>
                <time dateTime={startDate} itemProp="startDate">
                  {startFormatDate}
                </time> - <time dateTime={endDate} itemProp={endDate && 'endDate'}>
                  {endFormatDate}
                </time> 
                <Media from={380}> / </Media>
                <Media till={380}><br /></Media>
                <span className={styles.type}>
                  {type}
                </span> <span className={styles.duration}>
                  ({duration}{!endDate && '+'})
                </span>
              </p>
            </Media>
            <div className={styles.description} itemProp={isEducation ? 'review' :'description'}>
              {render(description, COMPONENTS_MAP)}
            </div>
          </div>
        </Fade>
      </li>
    </SbEditable>
  )
}
